import * as React from 'react';
import { useEffect } from 'react';

import { useFloating, autoUpdate, size, Placement, offset, Strategy } from '@floating-ui/react-dom';
import { Portal } from 'react-portal';
import styled from 'styled-components';

import { usePrevious } from '../../../ModernScripts/Spotlight/Hooks/usePrevious';
import { MenuList, MenuListProps, InteractionModes, MenuTypes, Option, OptionGroup, Section } from '../menu-list';
import { SelectMenuButton, SelectMenuButtonProps } from './atoms/select-menu-button';

type triggerEl = React.ForwardRefExoticComponent<SelectMenuButtonProps & React.RefAttributes<HTMLButtonElement>>;

export enum SelectMenuActionTypes {
    CLOSED_MENU = 'CLOSED_MENU',
    HIGHLIGHTED_ITEM = 'HIGHLIGHTED_ITEM',
    UPDATE_VALUE = 'UPDATE_VALUE',
}

export interface ClosedMenuAction {
    type: SelectMenuActionTypes.CLOSED_MENU;
}

export interface HighlightedItemAction {
    type: SelectMenuActionTypes.HIGHLIGHTED_ITEM;
    payload: { highlight: string };
}

export interface UpdateValueAction {
    type: SelectMenuActionTypes.UPDATE_VALUE;
    payload: { value: string };
}

export type SelectMenuActions = ClosedMenuAction | HighlightedItemAction | UpdateValueAction;

export type AnyAction = { type: string; payload?: Record<string, any> };
export type ActionHandler<TAction extends AnyAction> = (action: TAction | AnyAction) => void;

export type SelectMenuProps = Omit<
    MenuListProps,
    | 'menuType'
    | 'focussedInputValue'
    | 'activeItem'
    | 'setActiveItem'
    | 'setChosenItem'
    | 'interactionMode'
    | 'setInteractionMode'
> & {
    id?: string;
    dataId?: string;
    open?: boolean;
    selectedItem?: string;
    Trigger?: triggerEl;
    onChange?: (value: string) => void;
    actionHandler?: ActionHandler<SelectMenuActions>;
    /**
     * Is it disabled?
     */
    disabled?: boolean;
    invalid?: boolean;
    /** the select button width */
    width?: string;
    /** the select button minWidth */
    minWidth?: string;
    /** the select button maxWidth */
    maxWidth?: string;
    /** where to place the menu relative to the trigger element */
    placement?: Placement;
    /** constrain the menu width to the trigger element width */
    matchTriggerWidth?: boolean;
    /** constrain the menu width to the available space */
    constrainWidth?: boolean;
    /** constrain the menu height to the available space */
    constrainHeight?: boolean;
};

interface SelectMenuList extends MenuListProps {
    position: Strategy;
    x: number;
    y: number;
    maxHeight: number | 'none';
}

// scroll outside select menu should close it.
const SelectMenuList = styled(MenuList)<SelectMenuList>`
    z-index: 1020;
    position: ${(props) => props.position};
    left: ${(props) => `${props.x}px`};
    top: ${(props) => `${props.y}px`};
    max-height: ${(props) => (props.maxHeight === 'none' ? 'none' : `${props.maxHeight}px`)};
`;

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noOp = () => {};

interface MenuProps extends SelectMenuList {
    isOpen: boolean;
    portalKey: string;
    closeMenu: ({ value }: { value?: string | undefined }) => void;
}

const Menu = React.forwardRef<HTMLElement, MenuProps>(
    (
        {
            id,
            dataId,
            position,
            x,
            y,
            maxHeight,
            menuScrollY,
            isOpen,
            node,
            portalKey,
            options,
            closeMenu,
            interactionMode,
            setInteractionMode,
            focussedInputValue,
            activeItem,
            setActiveItem,
            setChosenItem,
            hasFilter,
            setFilterValue,
            filterValue,
            isFilterFocussed,
            setIsFilterFocussed,
            headerText,
            density,
            fontWeight,
        },
        ref,
    ) => {
        return (
            <Portal node={node} key={portalKey}>
                {isOpen && (
                    <SelectMenuList
                        ref={ref}
                        id={id}
                        dataId={dataId}
                        node={node}
                        options={options}
                        onClose={closeMenu}
                        setChosenItem={setChosenItem}
                        interactionMode={interactionMode}
                        setInteractionMode={setInteractionMode}
                        focussedInputValue={focussedInputValue}
                        activeItem={activeItem}
                        setActiveItem={setActiveItem}
                        menuType={MenuTypes.Select}
                        hasFilter={hasFilter}
                        setFilterValue={setFilterValue}
                        filterValue={filterValue}
                        isFilterFocussed={isFilterFocussed}
                        setIsFilterFocussed={setIsFilterFocussed}
                        position={position}
                        x={x}
                        y={y}
                        maxHeight={maxHeight}
                        menuScrollY={menuScrollY}
                        headerText={headerText}
                        density={density}
                        fontWeight={fontWeight}
                    />
                )}
            </Portal>
        );
    },
);

Menu.displayName = 'Menu';

/**
 * A custom dropdown menu that behaves like an HTML select
 */
export const SelectMenu: React.FC<React.PropsWithChildren<SelectMenuProps>> = ({
    id = 'select-menu',
    dataId = 'select-menu',
    options,
    node = document.body,
    selectedItem = '',
    Trigger,
    actionHandler = noOp,
    onChange,
    open = false,
    width,
    minWidth,
    maxWidth,
    disabled,
    invalid,
    hasFilter,
    placement = 'bottom-start',
    matchTriggerWidth = true,
    constrainWidth = true,
    constrainHeight = true,
    headerText,
    density,
    fontWeight,
}) => {
    const [isOpen, setIsOpen] = React.useState(open);
    const [chosenItem, setChosenItem] = React.useState<string>(selectedItem);
    const [interactionMode, setInteractionMode] = React.useState<InteractionModes>(InteractionModes.Mouse);
    const focussedInputValue = React.useRef<string>(selectedItem);

    const [activeItem, setActiveItem] = React.useState<string>(selectedItem);
    const menuScrollY = React.useRef(0);
    const menuMaxHeight = React.useRef<number | 'none'>('none');
    const [filterValue, setFilterValue] = React.useState('');
    const [isFilterFocussed, setIsFilterFocussed] = React.useState(true);
    const [filteredOptions, setFilteredOptions] = React.useState(options);

    const isOpenRef = React.useRef(isOpen);

    /** Keeps track of the previously chosen item */
    const prevChosenItem = usePrevious(chosenItem);

    useEffect(() => {
        // if the onChange function is updated we don't want to run the effect, so ensure the chosenItem has actually changed
        if (chosenItem === prevChosenItem) {
            return;
        }
        if (typeof onChange === 'function') {
            onChange(chosenItem);
        }
    }, [chosenItem, onChange, prevChosenItem]);

    /**
     * unique key for the portal
     */
    const portalKey = 'select-menu-portal';

    const clickButton: React.MouseEventHandler<Element> = () => {
        if (isOpen) {
            // if it's already open, don't open again. This allows clicking the button to close it when it's open.
            setIsOpen(false);
            isOpenRef.current = false;
            return;
        }
        setIsOpen(true);
        setIsFilterFocussed(true);
        isOpenRef.current = isOpen;
    };

    const choseItemHandler = ({ value, scrollY }: { value: string; scrollY?: number }) => {
        setChosenItem(value);
        if (typeof scrollY !== 'undefined') {
            menuScrollY.current = scrollY;
        }
        // a select menu highlights an item before potentially confirming it as the value to update
        actionHandler({
            type: SelectMenuActionTypes.HIGHLIGHTED_ITEM,
            payload: { highlight: value },
        });
    };

    const closeMenu = ({ value }: { value?: string }) => {
        if (!isOpen) {
            // isn't open so no need to close
            return;
        }
        if (typeof value === 'undefined') {
            value = '';
        }
        setIsOpen(false);
        isOpenRef.current = false;
        actionHandler({ type: SelectMenuActionTypes.CLOSED_MENU });
        // only send UPDATE_VALUE action if the value has changed from prev
        if (prevChosenItem === value) {
            return;
        }
        // a select menu updates the value on close
        actionHandler({ type: SelectMenuActionTypes.UPDATE_VALUE, payload: { value } });
    };

    const exitWithoutSelection: EventListener = (e) => {
        // not open, so no need to close
        if (!isOpenRef.current) {
            return;
        }
        // scrolling the menu list itself, don't close
        if (e.target && (e.target as HTMLElement).id === `${id}-list`) {
            return;
        }
        // close the menu
        setIsOpen(false);
        isOpenRef.current = false;
        actionHandler({ type: SelectMenuActionTypes.CLOSED_MENU });
    };

    isOpenRef.current = isOpen;
    window.removeEventListener('resize', exitWithoutSelection);
    window.addEventListener('resize', exitWithoutSelection);
    document.removeEventListener('scroll', exitWithoutSelection, true);
    document.addEventListener('scroll', exitWithoutSelection, true);

    useEffect(() => {
        // when selectedItem prop changes update the active item
        focussedInputValue.current = selectedItem;
        setActiveItem(selectedItem);
        setChosenItem(selectedItem);
    }, [selectedItem]);

    const isSection = (arg: any): arg is Section => {
        return arg?.type === 'section';
    };

    const isOptionGroup = (item: any): item is OptionGroup => {
        return item?.label !== undefined;
    };

    useEffect(() => {
        // Filter the options when the filter or options change
        const transformedFilterValue = filterValue.toLowerCase().trim();

        const isFilterMatch = (value: string) => value.toLocaleLowerCase().includes(transformedFilterValue);
        const filterChildOptions = (group: OptionGroup) => {
            // If the group label matches the filter, include all child options
            if (isFilterMatch(group.label)) {
                return group;
            }

            // Else only include child options that match the filter
            const filteredChildOptions = group.options.filter((childOption) => isFilterMatch(childOption.text));

            return {
                label: group.label,
                options: filteredChildOptions,
            } as OptionGroup;
        };

        const filterSectionOptions = (section: Section) => {
            const filteredSections = section.options
                .map((option) => (isOptionGroup(option) ? filterChildOptions(option) : option))
                .filter((option) =>
                    isOptionGroup(option) ? option.options.length : isFilterMatch(option.text),
                ) as Array<Option | OptionGroup>;

            return { type: 'section', options: filteredSections } as Section;
        };

        const filteredOptions = options
            .map((option) =>
                isOptionGroup(option)
                    ? filterChildOptions(option)
                    : isSection(option)
                    ? filterSectionOptions(option)
                    : option,
            )
            .filter((option) =>
                isOptionGroup(option)
                    ? option.options.length
                    : isSection(option)
                    ? option.options.length
                    : isFilterMatch(option.text),
            );

        setFilteredOptions(filteredOptions);
    }, [filterValue, options]);

    // assumes all options have unique values as it finds the first matching value only (doesn't match on option text)
    const selectedOptionText = options
        .flatMap((item) => {
            if (isOptionGroup(item)) {
                return item.options.flat();
            } else if (isSection(item)) {
                return item.options.flatMap((sectionOption) =>
                    isOptionGroup(sectionOption) ? sectionOption.options.flat() : sectionOption,
                );
            } else {
                return item;
            }
        })
        .find((item) => item.value === chosenItem)?.text;

    const { x, y, reference, floating, strategy } = useFloating({
        placement,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(5),
            size({
                apply({ rects, availableWidth, availableHeight, elements }) {
                    if (matchTriggerWidth) {
                        // constrain width to the trigger button width
                        Object.assign(elements.floating.style, {
                            width: `${rects.reference.width}px`,
                        });
                    }
                    if (constrainWidth) {
                        // constrain to the available space
                        Object.assign(elements.floating.style, {
                            maxWidth: `${availableWidth}px`,
                        });
                    }
                    if (constrainHeight) {
                        menuMaxHeight.current = availableHeight;
                    } else {
                        menuMaxHeight.current = 'none';
                    }
                },
            }),
        ],
    });

    return (
        <>
            {Trigger && (
                <Trigger
                    ref={reference}
                    id={`${id}-button`}
                    dataId={`${dataId}-button`}
                    onClick={clickButton}
                    chosenItem={selectedOptionText}
                    disabled={disabled}
                    invalid={invalid}
                    width={width}
                    minWidth={minWidth}
                    maxWidth={maxWidth}
                />
            )}
            {!Trigger && (
                <SelectMenuButton
                    ref={reference}
                    id={`${id}-button`}
                    dataId={`${dataId}-button`}
                    onClick={clickButton}
                    chosenItem={selectedOptionText}
                    disabled={disabled}
                    invalid={invalid}
                    width={width}
                    minWidth={minWidth}
                    maxWidth={maxWidth}
                    placeholder={'Please select'}
                />
            )}
            <Menu
                id={id}
                dataId={dataId}
                isOpen={isOpen}
                ref={floating}
                position={strategy}
                x={x ?? 0}
                y={y ?? 0}
                maxHeight={menuMaxHeight.current}
                menuScrollY={menuScrollY}
                node={node}
                options={filteredOptions}
                closeMenu={closeMenu}
                interactionMode={interactionMode}
                setInteractionMode={setInteractionMode}
                focussedInputValue={focussedInputValue}
                activeItem={activeItem}
                setActiveItem={setActiveItem}
                menuType={MenuTypes.NavMenu}
                setChosenItem={choseItemHandler}
                hasFilter={hasFilter}
                setFilterValue={setFilterValue}
                filterValue={filterValue}
                isFilterFocussed={isFilterFocussed}
                setIsFilterFocussed={setIsFilterFocussed}
                portalKey={portalKey}
                headerText={headerText}
                density={density}
                fontWeight={fontWeight}
            />
        </>
    );
};

SelectMenu.displayName = 'SelectMenu';
